import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mail, Phone, MapPin, Clock, MessageSquare,
  Send, Building2, Globe2, CheckCircle, AlertCircle
} from 'lucide-react';

// Enhanced ContactCard with consistent styling and better hover states
const ContactCard = ({ icon: Icon, title, children }) => (
  <motion.div
    className="relative h-full"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4 }}
  >
    <div className="relative h-full overflow-hidden rounded-xl bg-gradient-to-br from-indigo-900/30 to-purple-900/30 p-[1px] transition-all duration-300 hover:from-indigo-600/40 hover:to-purple-600/40">
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 blur-xl" />
      <div className="relative h-full bg-gray-900/95 p-8 rounded-lg backdrop-blur-sm">
        <div className="mb-6 inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl">
          <Icon className="w-7 h-7 text-indigo-400" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-4">{title}</h3>
        <div className="space-y-2">{children}</div>
      </div>
    </div>
  </motion.div>
);

// Enhanced form status component
const FormStatus = ({ status }) => {
  if (!status) return null;
  
  const statusConfig = {
    sending: {
      icon: Send,
      message: "Sending your message...",
      className: "bg-indigo-500/20 border-indigo-500/30 text-indigo-400"
    },
    success: {
      icon: CheckCircle,
      message: "Message sent successfully! We'll get back to you soon.",
      className: "bg-green-500/20 border-green-500/30 text-green-400"
    },
    error: {
      icon: AlertCircle,
      message: "Something went wrong. Please try again.",
      className: "bg-red-500/20 border-red-500/30 text-red-400"
    }
  };

  const config = statusConfig[status];
  const StatusIcon = config.icon;

  return (
    <motion.div
      className={`mt-4 p-4 border rounded-lg flex items-center gap-3 ${config.className}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <StatusIcon className="w-5 h-5 flex-shrink-0" />
      <span>{config.message}</span>
    </motion.div>
  );
};

// Enhanced input component
const FormInput = ({ label, type = "text", name, value, onChange, required = true }) => (
  <div className="space-y-2">
    <label htmlFor={name} className="block text-sm font-medium text-white/90">
      {label}
    </label>
    {type === "textarea" ? (
      <textarea
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        rows="6"
        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/50
          focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all duration-200
          hover:border-white/20"
        required={required}
      />
    ) : (
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/50
          focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all duration-200
          hover:border-white/20"
        required={required}
      />
    )}
  </div>
);

const OfficeLocation = ({ city, country, address, image }) => (
  <motion.div
    className="relative group"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
  >
    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-indigo-900/50 to-purple-900/50 p-1">
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 blur-xl" />
      <div className="relative bg-gray-900 rounded-lg overflow-hidden">
        <div className="h-48 overflow-hidden">
          <img
            src={image}
            alt={`${city} office`}
            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
          />
        </div>
        <div className="p-6">
          <h3 className="text-xl font-bold text-white mb-2">{city}</h3>
          <p className="text-indigo-300 mb-4">{country}</p>
          <div className="flex items-start gap-2 text-gray-300">
            <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
            <p>{address}</p>
          </div>
        </div>
      </div>
    </div>
  </motion.div>
);

function Contact() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('sending');
    
    // Simulate API call
    setTimeout(() => {
      setSubmitStatus('success');
      setFormState({ name: '', email: '', subject: '', message: '' });
      
      // Reset status after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    }, 1500);
  };

  const handleChange = (e) => {
    setFormState(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="bg-gray-900 min-h-screen">
      {/* Hero Section - Updated with more subtle gradient */}
      <section className="relative min-h-[65vh] flex items-center">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-indigo-950 to-purple-950">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-20" />
        </div>
        
        <div className="relative container mx-auto px-4 py-20">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-block mb-6"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-indigo-500 blur-xl opacity-50" />
                <div className="relative bg-gradient-to-br from-indigo-500 to-purple-500 p-3 rounded-lg">
                  <MessageSquare className="w-10 h-10 text-white" />
                </div>
              </div>
            </motion.div>
            
            <motion.h1 
              className="text-4xl md:text-6xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              Get in Touch
            </motion.h1>
            
            <motion.p 
              className="text-xl text-gray-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Have questions? We're here to help. Contact our team for support,
              inquiries, or partnership opportunities.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods Section - Updated grid layout */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-indigo-950">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ContactCard icon={Phone} title="Call Us">
              <p className="text-gray-300">24/7 Support Line:</p>
              <a href="tel:+15867707725" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                +1 (586) 770-7725
              </a>
            </ContactCard>

            <ContactCard icon={Mail} title="Email Us">
              <p className="text-gray-300">Support:</p>
              <a href="mailto:david@loadlinkco.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                david@loadlinkco.com
              </a>
              <p className="text-gray-300 mt-2">Business Inquiries:</p>
              <a href="mailto:david@loadlinkco.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                david@loadlinkco.com
              </a>
            </ContactCard>

            <ContactCard icon={Clock} title="Business Hours">
              <p className="text-gray-300">Monday - Friday:</p>
              <p className="text-indigo-400">9:00 AM - 6:00 PM EST</p>
              <p className="text-gray-300 mt-2">Weekend Support:</p>
              <p className="text-indigo-400">24/7 Emergency Only</p>
            </ContactCard>
          </div>
        </div>
      </section>

      {/* Contact Form Section - Enhanced styling */}
      <section className="py-24 bg-gradient-to-b from-indigo-950 to-purple-950">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.form
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-900/30 to-purple-900/30 p-[1px]"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="relative bg-gray-900/95 p-10 rounded-xl backdrop-blur-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <FormInput
                  label="Name"
                  name="name"
                  value={formState.name}
                  onChange={handleChange}
                />
                <FormInput
                  label="Email"
                  type="email"
                  name="email"
                  value={formState.email}
                  onChange={handleChange}
                />
              </div>
              
              <FormInput
                label="Subject"
                name="subject"
                value={formState.subject}
                onChange={handleChange}
                className="mb-6"
              />

              <FormInput
                label="Message"
                type="textarea"
                name="message"
                value={formState.message}
                onChange={handleChange}
                className="mb-6"
              />

              <motion.button
                type="submit"
                className="w-full px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg
                  font-medium relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed
                  hover:from-indigo-500 hover:to-purple-500 transition-all duration-300"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                disabled={submitStatus === 'sending'}
              >
                <span className="relative flex items-center justify-center gap-2">
                  {submitStatus === 'sending' ? (
                    <>Sending... <span className="animate-spin">⚬</span></>
                  ) : (
                    <>Request Demo <Send className="w-5 h-5" /></>
                  )}
                </span>
              </motion.button>

              <FormStatus status={submitStatus} />
            </div>
          </motion.form>
        </div>
      </section>

      {/* Office Location Section */}
      <section className="py-20 bg-gradient-to-b from-purple-900 to-gray-900">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">Visit Us</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Stop by our office or reach out to schedule a visit
            </p>
          </motion.div>

          <div className="flex justify-center">
            <div className="w-full max-w-2xl">
              <OfficeLocation
                city="Warren"
                country="Michigan, United States"
                address="13950 Frazho Rd, Warren, MI 48089"
                image="https://images.unsplash.com/photo-1497366216548-37526070297c"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact; 